/**
 * APP Plus "Actions" card — the MIPS Gap Management Actions card
 * (GapRecommendationsCard) verbatim, scoped to the ACO (owner 2026-07-14 PM).
 * Backend: /api/acos/:acoId/goals (aco_goals table; writes year-locked).
 *
 * Goals share the React Query cache with useAppPlusScoring's prefetch so the
 * first QPS paint already has rows (no late "mount" after the Quality Summary).
 */
import { useState, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { Check, Plus, Trash2, X, Loader2 } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { useAcoGoals } from '@/hooks/queries/useMipsQueries';
import { qk } from '@/lib/query-keys';

interface AcoGoal {
  id: number;
  aco_id: number;
  title: string;
  completed: boolean;
  completed_at: string | null;
  created_at: string;
  sort_order: number;
}

export function AcoActionsCard({ acoId }: { acoId: number }) {
  const { programYear, isCurrentYearLocked } = useProgramYear();
  const queryClient = useQueryClient();
  const goalsKey = qk.acoGoals(acoId, programYear);
  const goalsQ = useAcoGoals(acoId, programYear);

  const goals = (goalsQ.data ?? []) as AcoGoal[];
  const isLoading = goalsQ.isPending && !goalsQ.data;
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newTaskText, setNewTaskText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (isAdding && inputRef.current) inputRef.current.focus(); }, [isAdding]);

  const setGoalsCache = (updater: (prev: AcoGoal[]) => AcoGoal[]) => {
    queryClient.setQueryData<AcoGoal[]>(goalsKey, (old) => updater(old ?? []));
  };

  const handleAddTask = async () => {
    if (!newTaskText.trim()) return;
    setIsSaving(true);
    try {
      const newGoal = await apiClient.post(`/api/acos/${acoId}/goals?year=${programYear}`, {
        title: newTaskText.trim(),
      });
      setGoalsCache((prev) => [...prev, newGoal]);
      setNewTaskText('');
      setIsAdding(false);
    } catch (error) {
      console.error('Failed to create ACO goal:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSaving) handleAddTask();
    else if (e.key === 'Escape') { setIsAdding(false); setNewTaskText(''); }
  };

  const toggleComplete = async (goal: AcoGoal) => {
    const newCompleted = !goal.completed;
    setGoalsCache((prev) =>
      prev.map((g) => (g.id === goal.id ? { ...g, completed: newCompleted } : g)));
    try {
      await apiClient.request(`/api/acos/${acoId}/goals/${goal.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed: newCompleted }),
      });
    } catch {
      setGoalsCache((prev) =>
        prev.map((g) => (g.id === goal.id ? { ...g, completed: !newCompleted } : g)));
    }
  };

  const deleteGoal = async (goalId: number) => {
    const previousGoals = goals;
    setGoalsCache((prev) => prev.filter((g) => g.id !== goalId));
    try {
      await apiClient.delete(`/api/acos/${acoId}/goals/${goalId}`);
    } catch {
      queryClient.setQueryData(goalsKey, previousGoals);
    }
  };

  const activeGoals = goals.filter((g) => !g.completed);
  const completedGoals = goals.filter((g) => g.completed);

  return (
    <Card className="rounded-xl border-accent bg-accent/5 mt-4">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base md:text-lg font-semibold">Actions</CardTitle>
          {!isAdding && !isCurrentYearLocked && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsAdding(true)}
              className="h-7 px-2 text-xs text-muted-foreground hover:text-primary"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add
            </Button>
          )}
        </div>
        <Separator className="mt-2" />
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid gap-3 md:grid-cols-3">
          {isLoading && (
            <>
              <div className="min-h-[52px] rounded-lg bg-white/40 animate-pulse" aria-hidden />
              <div className="min-h-[52px] rounded-lg bg-white/40 animate-pulse" aria-hidden />
            </>
          )}
          {activeGoals.map((goal) => (
            <div key={goal.id}
              className="group flex items-start gap-3 p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg min-h-[52px]">
              <button
                onClick={() => toggleComplete(goal)}
                disabled={isCurrentYearLocked}
                className={cn(
                  'flex-shrink-0 h-5 w-5 mt-0.5 rounded-full border-2 transition-all',
                  'border-slate-300 dark:border-slate-600',
                  'hover:border-primary hover:bg-primary/5',
                  'flex items-center justify-center'
                )}
              >
                <Check className="h-3 w-3 text-transparent" />
              </button>
              <span className="flex-1 text-sm leading-snug break-words">{goal.title}</span>
              <button
                onClick={() => deleteGoal(goal.id)}
                disabled={isCurrentYearLocked}
                className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
              </button>
            </div>
          ))}

          {isAdding && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-white/50 dark:bg-slate-800/50 border-2 border-dashed border-primary/30">
              <div className="flex-shrink-0 h-5 w-5 rounded-full border-2 border-slate-200 dark:border-slate-600" />
              <Input
                ref={inputRef}
                value={newTaskText}
                onChange={(e) => setNewTaskText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g., Confirm CAHPS vendor kickoff..."
                className="flex-1 h-8 border-0 bg-transparent p-0 text-sm focus-visible:ring-0 placeholder:text-slate-400"
                disabled={isSaving}
              />
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={handleAddTask}
                  disabled={!newTaskText.trim() || isSaving}
                  className="h-7 w-7 p-0 text-primary hover:bg-primary/10">
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                </Button>
                <Button size="sm" variant="ghost"
                  onClick={() => { setIsAdding(false); setNewTaskText(''); }}
                  disabled={isSaving}
                  className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {activeGoals.length === 0 && completedGoals.length === 0 && !isAdding && !isLoading && (
            <button
              onClick={() => !isCurrentYearLocked && setIsAdding(true)}
              className="md:col-span-3 p-4 rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700 hover:border-primary/40 hover:bg-primary/5 transition-colors group"
            >
              <div className="flex flex-col items-center gap-1 text-slate-400 group-hover:text-primary">
                <Plus className="h-5 w-5" />
                <span className="text-xs font-medium">Add an action</span>
              </div>
            </button>
          )}

          {completedGoals.map((goal) => (
            <div key={goal.id}
              className="group flex items-start gap-3 p-3 rounded-lg bg-white/30 dark:bg-slate-800/30 min-h-[52px]">
              <button
                onClick={() => toggleComplete(goal)}
                disabled={isCurrentYearLocked}
                className={cn(
                  'flex-shrink-0 h-5 w-5 mt-0.5 rounded-full transition-all',
                  'bg-emerald-500 border-2 border-emerald-500',
                  'flex items-center justify-center',
                  'hover:bg-emerald-400 hover:border-emerald-400'
                )}
              >
                <Check className="h-3 w-3 text-white" />
              </button>
              <span className="flex-1 text-sm text-slate-400 line-through leading-snug break-words">
                {goal.title}
              </span>
              <button
                onClick={() => deleteGoal(goal.id)}
                disabled={isCurrentYearLocked}
                className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Trash2 className="h-3.5 w-3.5 text-slate-300 hover:text-red-500" />
              </button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
